
import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { PlusCircle, Book } from "lucide-react";
import { Solution } from "@/types/solutions";

interface SolutionGridViewProps {
  solutions: Solution[];
  addToCart: (solution: Solution) => void;
  cart: Solution[];
  getAgencyColor: (agency: string) => string;
  getCommercialTypeColor: (type: string) => string;
}

const SolutionGridView: React.FC<SolutionGridViewProps> = ({
  solutions,
  addToCart,
  cart,
  getAgencyColor,
  getCommercialTypeColor
}) => {
  const navigate = useNavigate();

  const handleViewSolution = (solutionId: string) => {
    navigate(`/solution/${solutionId}`);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {solutions.map((solution) => (
        <Card key={solution.id} className="overflow-hidden hover:shadow-lg transition-shadow">
          {/* Asset Cover Image */}
          {solution.nftCoverImageUrl ? (
            <div 
              className="h-48 bg-cover bg-center cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundImage: `url(${solution.nftCoverImageUrl})` }}
              onClick={() => handleViewSolution(solution.id)}
            />
          ) : solution.image ? (
            <div 
              className="h-48 bg-cover bg-center cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundImage: `url(${solution.image})` }}
              onClick={() => handleViewSolution(solution.id)}
            />
          ) : (
            <div 
              className="h-48 bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={() => handleViewSolution(solution.id)}
            >
              <Book className="h-16 w-16 text-gray-400" />
            </div>
          )}
          
          <div className="p-4">
            <div className="flex justify-between items-start mb-3">
              <Badge variant="outline" className={getAgencyColor(solution.agency)}>
                {solution.agency}
              </Badge>
              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                {solution.commercialType}
              </Badge>
            </div>
            <h3 
              className="font-medium mb-1 cursor-pointer hover:text-blue-600"
              onClick={() => handleViewSolution(solution.id)}
            >
              {solution.title}
            </h3>
            <p className="text-xs text-gray-500 mb-1">{solution.category}</p>
            {solution.capturedDate && (
              <p className="text-xs text-gray-400 mb-2">Captured on {solution.capturedDate}</p>
            )}
            <p className="text-sm mb-4 text-gray-600 line-clamp-2">{solution.description}</p>
            <div className="flex justify-between items-center mb-3">
              <span className="font-semibold">${solution.price.toLocaleString()}</span>
              <Button 
                size="sm" 
                onClick={() => addToCart(solution)}
                disabled={cart.some(item => item.id === solution.id)}
              >
                {cart.some(item => item.id === solution.id) ? 
                  'Added' : 
                  <PlusCircle className="h-4 w-4" />
                }
              </Button>
            </div>
            
            {/* Creator Section */}
            <div className="pt-3 border-t flex items-center gap-2">
              {solution.creatorAvatar && (
                <Avatar className="h-6 w-6">
                  <AvatarImage src={solution.creatorAvatar} alt={solution.creator} />
                  <AvatarFallback className="text-xs">
                    {solution.creator.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="text-xs text-gray-500">
                Created by <span className="text-gray-700 font-medium">{solution.creator}</span>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default SolutionGridView;
